import { cx } from '@orn/shared-web/utils/cx';
import formatElapsed from '@orn/shared-web/utils/datetime/formatElapsed';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';
import * as React from 'react';

import { IdentityMatchResult, PaymentMethodType } from '../../__generated__/globalTypes';
import InspectLink from '../../components/InspectLink';
import Table from '../../components/Table';

import { Absent, Field, FieldList, Timestamp } from './AccountFields';
import { NameOrId } from './AccountLinks';
import {
  PAYMENT_METHOD_TYPE_LABELS,
  formatIdentityMatch,
  formatMandateTerms,
  formatPaymentMethodStatus,
  formatRevocationChannel,
  formatSubscriptionInterval,
} from './accountsView';
import SubscriptionBadge from './SubscriptionBadge';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';
import type { PaymentMethodTone } from './accountsView';

type Billing = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['billing'];
type Subscription = Billing['subscriptions'][number];
type PaymentMethod = Billing['paymentMethods'][number];
type Mandate = PaymentMethod['mandates'][number];

const TONE_CLASS: Record<PaymentMethodTone, string> = {
  error: 'bg-error-primary text-fg-error-primary',
  warning: 'bg-warning-primary text-fg-warning-primary',
  success: 'bg-success-primary text-fg-success-primary',
  neutral: 'border border-border-primary text-fg-tertiary',
};

interface IBillingPanelProps {
  billing: Billing;
}

/**
 * The household's billing: its Stripe customer, every subscription row, and
 * every payment method with the mandates recorded against it, newest first.
 *
 * Reports only what is stored; nothing here asks Stripe.
 */
const BillingPanel = ({ billing }: IBillingPanelProps) => {
  const stripeCustomerId = billing.stripeCustomerId ?? null;

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <FieldList>
        <Field label="Stripe customer" mono={stripeCustomerId !== null}>
          {stripeCustomerId ?? <Absent>None</Absent>}
        </Field>
      </FieldList>

      <SubsectionHeading label="Subscriptions" count={billing.subscriptions.length} />
      {billing.subscriptions.length === 0 ? (
        <Empty>No subscriptions on record.</Empty>
      ) : (
        <SubscriptionsTable subscriptions={billing.subscriptions} />
      )}

      <SubsectionHeading label="Payment methods" count={billing.paymentMethods.length} />
      {billing.paymentMethods.length === 0 ? (
        <Empty>No payment methods on record.</Empty>
      ) : (
        <div className="flex flex-col gap-5">
          {billing.paymentMethods.map((method) => (
            <PaymentMethodBlock key={method.id} method={method} />
          ))}
        </div>
      )}
    </section>
  );
};

interface ISubscriptionsTableProps {
  subscriptions: Subscription[];
}

const SubscriptionsTable = ({ subscriptions }: ISubscriptionsTableProps) => (
  <Table className="table-fixed">
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell className="w-[7.5rem]">Status</Table.HeaderCell>
        <Table.HeaderCell>Plan</Table.HeaderCell>
        <Table.HeaderCell className="w-[11.5rem]">Period end</Table.HeaderCell>
        <Table.HeaderCell className="w-[14.5rem]">Stripe subscription</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {subscriptions.map((subscription) => (
        <SubscriptionRow key={subscription.id} subscription={subscription} />
      ))}
    </Table.Body>
  </Table>
);

interface ISubscriptionRowProps {
  subscription: Subscription;
}

const SubscriptionRow = ({ subscription }: ISubscriptionRowProps) => {
  const periodEnd = subscription.currentPeriodEnd ?? null;
  const pendingIntervalAt = subscription.pendingIntervalAt ?? null;
  const switchClaimedAt = subscription.instrumentSwitchClaimedAt ?? null;
  const stripeSubscriptionId = subscription.stripeSubscriptionId ?? null;
  const createdAt = formatPacificDateTime(subscription.createdAt);
  const pendingSwitch =
    pendingIntervalAt === null
      ? ''
      : ` · switches ${formatPacificDateTime(pendingIntervalAt)} (${formatElapsed(pendingIntervalAt)})`;

  return (
    <Table.Row>
      <Table.Cell>
        <div className="flex flex-col items-start gap-1">
          <SubscriptionBadge status={subscription.status} />
          {subscription.cancelAtPeriodEnd && <ToneBadge tone="warning">cancels</ToneBadge>}
          {switchClaimedAt !== null && (
            <ToneBadge
              tone="warning"
              title={`A payment-account switch claimed this row ${formatPacificDateTime(switchClaimedAt)}; one still set long after is a switch that crashed`}
            >
              switching
            </ToneBadge>
          )}
          <span className="text-xs">
            <InspectLink id={subscription.id} short />
          </span>
        </div>
      </Table.Cell>
      <Table.Cell title={`${formatSubscriptionInterval(subscription)} · ${subscription.priceId}${pendingSwitch}`}>
        <div className="truncate">{formatSubscriptionInterval(subscription)}</div>
        <div className="text-fg-tertiary truncate text-xs">
          <span className="font-mono">{subscription.priceId}</span>
          {pendingSwitch}
        </div>
      </Table.Cell>
      <Table.Cell muted className="truncate text-xs">
        {periodEnd === null ? <Absent>None</Absent> : <Timestamp instant={periodEnd} />}
      </Table.Cell>
      <Table.Cell title={`Created ${createdAt} · updated ${formatPacificDateTime(subscription.updatedAt)}`}>
        <div className={cx('truncate text-xs', stripeSubscriptionId !== null && 'font-mono')}>
          {stripeSubscriptionId === null ? (
            <span
              title={`An attempt can hold a Stripe subscription this row never recorded: metadata.localSubscriptionId ${subscription.id}`}
            >
              <Absent>Not recorded</Absent>
            </span>
          ) : (
            <InspectLink id={stripeSubscriptionId} />
          )}
        </div>
        <div className="text-fg-tertiary truncate text-xs">
          created <Timestamp instant={subscription.createdAt} />
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

interface IPaymentMethodBlockProps {
  method: PaymentMethod;
}

const PaymentMethodBlock = ({ method }: IPaymentMethodBlockProps) => {
  const status = formatPaymentMethodStatus(method.status);
  const financialAccountName = method.financialAccountName ?? null;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <span className="text-fg-primary text-sm font-semibold">
          {method.institutionName ?? PAYMENT_METHOD_TYPE_LABELS[method.type]}
          {method.mask && <span className="text-fg-tertiary font-mono font-normal"> ···{method.mask}</span>}
        </span>
        <ToneBadge tone={status.tone}>{status.label}</ToneBadge>
        {method.isDefault && <ToneBadge tone="neutral">default</ToneBadge>}
      </div>

      <FieldList>
        <Field label="Type">{PAYMENT_METHOD_TYPE_LABELS[method.type]}</Field>
        {method.type === PaymentMethodType.UsBankAccount && (
          <Field label="Linked account">{financialAccountName ?? <Absent>Not linked</Absent>}</Field>
        )}
        <Field label="Stripe payment method" mono>
          <InspectLink id={method.stripePaymentMethodId} />
        </Field>
        <Field label="Payment method id" mono>
          <InspectLink id={method.id} />
        </Field>
        <Field label="Added">
          <Timestamp instant={method.createdAt} />
        </Field>
        <Field label="Updated">
          <Timestamp instant={method.updatedAt} />
        </Field>
      </FieldList>

      {method.mandates.length === 0 ? (
        <Empty>No mandate on record.</Empty>
      ) : (
        <div className="border-border-primary ml-1 flex flex-col gap-3 border-l pl-3">
          {method.mandates.map((mandate) => (
            <MandateBlock key={mandate.id} mandate={mandate} />
          ))}
        </div>
      )}
    </div>
  );
};

interface IMandateBlockProps {
  mandate: Mandate;
}

/** One authorization, in the shape support hands a bank asking for proof of it. */
const MandateBlock = ({ mandate }: IMandateBlockProps) => {
  const identityMatch = formatIdentityMatch(mandate.identityMatchResult);
  const ip = mandate.ip ?? null;
  const userAgent = mandate.userAgent ?? null;
  const revokedAt = mandate.revokedAt ?? null;
  const revocationChannel = mandate.revocationChannel ?? null;

  return (
    <FieldList>
      <Field label="Accepted by">
        <NameOrId name={mandate.acceptedByName} />
      </Field>
      <Field label="Accepted">
        <Timestamp instant={mandate.acceptedAt} />
      </Field>
      <Field label="Terms">{formatMandateTerms(mandate)}</Field>
      <Field label="Text version" mono>
        {mandate.mandateTextVersion}
      </Field>
      <Field label="Identity match">
        {identityMatch === null ? (
          <Absent>Not checked</Absent>
        ) : (
          <span
            className={cx(mandate.identityMatchResult === IdentityMatchResult.Mismatch && 'text-fg-warning-primary')}
          >
            {identityMatch}
          </span>
        )}
      </Field>
      <Field label="Third-party ack">{mandate.thirdPartyAck ? 'Yes' : 'No'}</Field>
      <Field label="IP" mono={ip !== null}>
        {ip ?? <Absent>Not recorded</Absent>}
      </Field>
      <Field label="User agent" mono={userAgent !== null}>
        {userAgent ?? <Absent>Not recorded</Absent>}
      </Field>
      <Field label="Revoked">
        {revokedAt === null ? (
          <Absent>Not revoked</Absent>
        ) : (
          <>
            <Timestamp instant={revokedAt} />
            {revocationChannel !== null && ` via ${formatRevocationChannel(revocationChannel)}`}
          </>
        )}
      </Field>
      <Field label="Mandate id" mono>
        <InspectLink id={mandate.id} />
      </Field>
    </FieldList>
  );
};

interface ISubsectionHeadingProps {
  label: string;
  count: number;
}

const SubsectionHeading = ({ label, count }: ISubsectionHeadingProps) => (
  <h3 className="text-fg-secondary flex items-baseline gap-2 pt-1 text-xs font-semibold uppercase tracking-wider">
    {label}
    <span className="text-fg-tertiary font-normal">{count}</span>
  </h3>
);

interface IToneBadgeProps {
  tone: PaymentMethodTone;
  title?: string;
  children: React.ReactNode;
}

const ToneBadge = ({ tone, title, children }: IToneBadgeProps) => (
  <span
    className={cx('whitespace-nowrap px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', TONE_CLASS[tone])}
    title={title}
  >
    {children}
  </span>
);

interface IEmptyProps {
  children: React.ReactNode;
}

const Empty = ({ children }: IEmptyProps) => <div className="text-fg-tertiary text-xs italic">{children}</div>;

export default BillingPanel;
